import { staffAuthHeaders } from '@/lib/api/staffAuthHeaders'
/**
 * Test Booking Service
 * Handles API calls for the admin testing dashboard - test bookings
 */

const API_BASE = import.meta.env.VITE_API_URL || ''

export interface TestBooking {
  id: string
  event_date: string
  event_time: string
  event_type: string
  package_type: string
  total_amount: number
  booking_status: string
  payment_status: string
  lifecycle_stage: string | null
  location_city: string
  location_state: string
  created_at: string
  is_test: boolean
  photos_upload_link: string | null
  videos_upload_link: string | null
  raws_upload_link: string | null
  hours_booked: number | null
  customer: {
    id: string
    full_name: string
    email: string
  } | null
  photographer: {
    id: string
    user: {
      id: string
      full_name: string
      email: string
    }
  } | null
}

export interface PromotionDetails {
  id: string
  code: string
  name?: string
  discount_type: 'money_off' | 'percentage_off' | 'addon_free'
  discount_value: number | null
  free_addons?: string[]
}

export interface CreateTestBookingData {
  customer_id: string
  photographer_id: string
  event_date: string
  event_time?: string
  event_type?: string
  package_type?: string
  hours_booked?: number
  total_amount?: number
  location_city?: string
  location_state?: string
  add_ons?: string[]
  promotionDetails?: PromotionDetails
}

export interface User {
  id: string
  full_name: string
  email: string
  role: string
}

export interface Photographer {
  id: string
  status: string
  user: {
    id: string
    full_name: string
    email: string
  }
}

class TestBookingService {
  /**
   * Fetch all test bookings
   */
  async getTestBookings(): Promise<TestBooking[]> {
    try {
      const response = await fetch(`${API_BASE}/api/testing/bookings`, { headers: await staffAuthHeaders() })
      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch test bookings')
      }

      return result.data || []
    } catch (error) {
      console.error('[TestBookingService] Error fetching test bookings:', error)
      return []
    }
  }

  /**
   * Create a new test booking
   */
  async createTestBooking(data: CreateTestBookingData): Promise<TestBooking | null> {
    try {
      const response = await fetch(`${API_BASE}/api/testing/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await staffAuthHeaders())
        },
        body: JSON.stringify(data)
      })

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || 'Failed to create test booking')
      }

      return result.data
    } catch (error) {
      console.error('[TestBookingService] Error creating test booking:', error)
      throw error
    }
  }

  /**
   * Delete a test booking
   */
  async deleteTestBooking(bookingId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}/api/testing/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: await staffAuthHeaders()
      })

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete test booking')
      }

      return true
    } catch (error) {
      console.error('[TestBookingService] Error deleting test booking:', error)
      throw error
    }
  }

  /**
   * Advance a test booking to a specific stage
   */
  async advanceStage(bookingId: string, stage: string, adminId?: string): Promise<TestBooking | null> {
    try {
      const response = await fetch(`${API_BASE}/api/testing/bookings/${bookingId}/stage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await staffAuthHeaders())
        },
        body: JSON.stringify({ stage, admin_id: adminId })
      })

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || 'Failed to advance booking stage')
      }

      return result.data
    } catch (error) {
      console.error('[TestBookingService] Error advancing booking stage:', error)
      throw error
    }
  }

  /**
   * Reset a test booking to initial stage
   */
  async resetBooking(bookingId: string, adminId?: string): Promise<TestBooking | null> {
    try {
      const response = await fetch(`${API_BASE}/api/testing/bookings/${bookingId}/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await staffAuthHeaders())
        },
        body: JSON.stringify({ admin_id: adminId })
      })

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || 'Failed to reset booking')
      }

      return result.data
    } catch (error) {
      console.error('[TestBookingService] Error resetting booking:', error)
      throw error
    }
  }

  /**
   * Fetch users for dropdown selection
   */
  async getUsers(): Promise<User[]> {
    try {
      const response = await fetch(`${API_BASE}/api/testing/users`, { headers: await staffAuthHeaders() })
      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch users')
      }

      return result.data || []
    } catch (error) {
      console.error('[TestBookingService] Error fetching users:', error)
      return []
    }
  }

  /**
   * Fetch photographers for dropdown selection
   */
  async getPhotographers(): Promise<Photographer[]> {
    try {
      const response = await fetch(`${API_BASE}/api/testing/photographers`, { headers: await staffAuthHeaders() })
      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch photographers')
      }

      return result.data || []
    } catch (error) {
      console.error('[TestBookingService] Error fetching photographers:', error)
      return []
    }
  }
}

export const testBookingService = new TestBookingService()
